/**
 * SS-07 (#1705): トライアル中 WS の介入リスト テーブル。
 *
 * 「15分相談しますか？」と声をかけるべき会社を 1 画面で見るための面
 * （`docs/growth/self-serve-strategy.md` §4）。並び順は API が「理由が多い順 → 登録が古い順」
 * で返すので、ここでは並べ替えない。
 *
 * ## 守るライン #1（個人情報）
 * 会社名・slug・メール・担当者名の列を持たない。API もそれらを返さない。
 * 識別は workspace id で行い、詳細は workspace 詳細画面へのリンクで辿る。
 *
 * `Workspaces/WorkspaceTable.tsx` と同じく素の `<table>` + Tailwind（TanStack Table は未導入）。
 */
import { Link } from 'react-router';
import type { TrialFlagKey, TrialListRow } from '../../lib/trials-api';
import {
  QUICKSTART_TOTAL,
  TRIAL_FLAG_BUTTONS,
  TRIAL_REASON_LABEL,
  trialDaysRemaining,
} from './labels';

export interface TrialTableProps {
  rows: TrialListRow[];
  /** 残り日数の基準時刻（テストで固定するために受ける）。 */
  now?: Date;
  onFlagChange: (workspaceId: string, flagKey: TrialFlagKey, enabled: boolean) => void;
}

/** 残り日数が少ないほど強い色（3 日以下は赤）。 */
function daysBadgeClass(days: number | null): string {
  if (days === null) return 'bg-slate-100 text-slate-600';
  if (days <= 3) return 'bg-rose-100 text-rose-800';
  if (days <= 7) return 'bg-amber-100 text-amber-800';
  return 'bg-sky-100 text-sky-800';
}

export function TrialTable({ rows, now, onFlagChange }: TrialTableProps) {
  if (rows.length === 0) {
    return (
      <p className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600">
        トライアル中のワークスペースがありません。
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table data-testid="trial-table" className="w-full border-collapse text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs text-slate-600">
          <tr>
            <th className="px-3 py-2 font-medium">workspace</th>
            <th className="px-3 py-2 font-medium">残り</th>
            <th className="px-3 py-2 font-medium">最短ガイド</th>
            <th className="px-3 py-2 font-medium">見込み客</th>
            <th className="px-3 py-2 font-medium">追客日</th>
            <th className="px-3 py-2 font-medium">7d ログイン</th>
            <th className="px-3 py-2 font-medium">料金閲覧</th>
            <th className="px-3 py-2 font-medium">介入理由</th>
            <th className="px-3 py-2 font-medium">手動フラグ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const days = trialDaysRemaining(row.trialEndsAt, now);
            const flagged = new Set(row.manualFlags.map((f) => f.flagKey));
            return (
              <tr key={row.workspaceId} className="border-b border-slate-100 last:border-b-0">
                <td className="px-3 py-2 font-mono text-xs">
                  <Link
                    to={`/workspaces/${row.workspaceId}`}
                    className="text-sky-700 hover:underline"
                  >
                    {row.workspaceId.slice(0, 8)}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-xs ${daysBadgeClass(days)}`}
                  >
                    {days === null ? '—' : `${days}日`}
                  </span>
                </td>
                <td className="px-3 py-2 tabular-nums">
                  {row.quickstartDoneCount} / {QUICKSTART_TOTAL}
                </td>
                <td className="px-3 py-2 tabular-nums">{row.leadCount}</td>
                <td className="px-3 py-2">{row.hasFollowUpSet ? '有' : '—'}</td>
                <td className="px-3 py-2 tabular-nums">{row.loginDaysLast7}</td>
                <td className="px-3 py-2">
                  {/* 隣の「最短ガイド 2 / 4」と同じ `/` を使うと分数に見えるので、
                      申込済は別バッジにして区切り文字を共有しない。 */}
                  <span className="tabular-nums">{row.billingViewedCount}</span>
                  {row.checkoutStarted && (
                    <span className="ml-1.5 inline-block rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">
                      申込済
                    </span>
                  )}
                </td>
                <td data-testid="trial-reasons" className="px-3 py-2">
                  {row.reasons.length === 0 ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {row.reasons.map((reason) => (
                        <span
                          key={reason}
                          className="inline-block rounded bg-rose-50 px-2 py-0.5 text-xs text-rose-700"
                        >
                          {TRIAL_REASON_LABEL[reason]}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className="flex gap-1">
                    {TRIAL_FLAG_BUTTONS.map(({ key, label }) => {
                      const on = flagged.has(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          aria-pressed={on}
                          onClick={() => onFlagChange(row.workspaceId, key, !on)}
                          className={`rounded border px-2 py-0.5 text-xs ${
                            on
                              ? 'border-sky-300 bg-sky-100 text-sky-800'
                              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
